import React, { useState } from "react";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import XIcon from "src/icons/XIcon";
import TrashIcon from "src/icons/TrashIcon";
import styled from "@emotion/styled";
import theme from "src/theme";
import { STATUS } from "src/components/collections/consts";
import BulkActionsDialog from "./BulkActionsDialog";
import { BULK_ACTIONS_TYPES } from "./BulkActions.consts";
import HyroIconButton from "@hyro-components/hyro-icon-button/HyroIconButton";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import BulkActionColumn from "src/components/collections/bulk-actions/BulkActionColumn";
import DuplicateIcon from "src/icons/DuplicateIcon";

const fieldTypesWithoutBulkActions = [FIELD_TYPES.MULTI_LANGUAGE_TEXT, FIELD_TYPES.ACTION];

const StyledTypographyButton = styled(Typography)`
  display: flex;
  align-content: baseline;
  cursor: pointer;
  line-height: 22px;
  padding: ${theme.spacing(1)} ${theme.spacing(1.5)};
`;

const StyledTableField = styled.span`
  display: flex;
  flex: none;
  cursor: pointer;
  height: 38px;
  margin-inline: 12px;

  &:hover {
    background-color: ${theme.palette.action.hover};
    border-radius: 8px;
  }
`;

const BulkActionsPanel = ({
  columns,
  selectedRows,
  setSelectedRows,
  getBulkActionEditCell,
  handleBulkRowsDelete,
  handleBulkRowsDuplicate,
  enableAddDeleteRows,
  focusedField,
  setFocusedField,
  setSearchText,
  tableRef
}) => {
  const [dialogActionType, setDialogActionType] = useState(null); // null | 'delete' | 'duplicate'

  const isDialogOpen = Boolean(dialogActionType);

  columns = columns.filter(column => column.id !== STATUS && !fieldTypesWithoutBulkActions.includes(column.type) && column.parentType !== FIELD_TYPES.FILE);

  return (
    <>
      <StyledStack data-testid="bulk-actions-panel">
        {/* title*/}
        <Stack direction="row" alignItems="center" flex="none">
          <IconButton
            data-testid="bulk-actions-close-button"
            size='small'
            sx={{ mr: "16px" }}
            onClick={() => {
              setSearchText?.("");
              tableRef?.current?.setSelectedRows([]);
            }}>
            <XIcon />
          </IconButton>
          <Typography variant="subtitle1" data-testid="bulk-actions-title">
            {`${selectedRows.length} selected`}
          </Typography>
        </Stack>
        {/* fields*/}
        <Stack
          marginLeft="-22px"
          paddingBlock="10px"
          direction="row"
          alignItems="center"
          overflow="scroll"
          flex="1"
          divider={<Divider orientation="vertical" sx={{ height: "20px" }} />}>
          {columns.map((column, index) => (
            <StyledTableField
              data-testid={`bulk-actions-field-${index}`}
              key={index}
              isFocused={focusedField === index}>
              {focusedField === index ?
                <BulkActionColumn>
                  {getBulkActionEditCell(column.key, selectedRows)}</BulkActionColumn> :
                <StyledTypographyButton
                  onClick={() => setFocusedField(index)}
                  key={index}
                  variant="body2">
                  {column.headerName}
                </StyledTypographyButton>}
            </StyledTableField>
          ))}
        </Stack>

        {/* buttons*/}
        {enableAddDeleteRows && <Stack direction="row" alignItems="center" gap={1}>
          <HyroIconButton
            tooltip="Duplicate"
            onClick={() => {
              if (selectedRows.length > 40) {
                setDialogActionType(BULK_ACTIONS_TYPES.DUPLICATE);
              } else {
                handleBulkRowsDuplicate(selectedRows);
              }
            }}
            data-testid="bulk-actions-duplicate-button"
          >
            <DuplicateIcon />
          </HyroIconButton>
          <HyroIconButton
            tooltip="Delete"
            onClick={() => setDialogActionType(BULK_ACTIONS_TYPES.DELETE)}
            data-testid="bulk-actions-delete-button"
          >
            <TrashIcon strokeOpacity="1" />
          </HyroIconButton>
        </Stack>}

      </StyledStack>

      <BulkActionsDialog
        open={isDialogOpen}
        actionType={dialogActionType}
        rowsCount={selectedRows.length}
        onCancel={() => setDialogActionType(null)}
        onConfirm={() => {
          if (dialogActionType === BULK_ACTIONS_TYPES.DELETE) {
            handleBulkRowsDelete(selectedRows);
            tableRef?.current?.setSelectedRows([]);
          } else if (dialogActionType === BULK_ACTIONS_TYPES.DUPLICATE) {
            handleBulkRowsDuplicate(selectedRows);
          }
          setDialogActionType(null);
        }}
      />
    </>
  );
};

const StyledStack = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  backgroundColor: theme.palette.system[0],
  boxShadow: theme.shadows[3],
  marginTop: "-8px",
  borderRadius: "8px",
  padding: "0 10px",
  marginBottom: theme.spacing(2),
  gap: "34px"
}));

export default BulkActionsPanel;
